import { has, includes } from "lodash";
import { AbstractDate } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { Metric } from "@linkfluence/models/metric";
import { Clause } from "@radarly/models/clause";
import {
  Dataviz,
  DatavizName,
  getDefaultDataviz,
} from "@radarly/models/dataviz";
import { Card, TermCard } from "..";
import { SearchFragmentTerm } from "@radarly/models/search";
import { isTermCardDataviz } from "../dataviz";
import { ComparisonPeriod } from "../period";
import { TermCardTerm } from "../term";
import { CardFactory, CardFragmentsFactory } from "./base";
import { ICardFactory } from "./model";
interface TermCardFactoryOptions {
  term?: TermCardTerm;
  from?: AbstractDate;
  to?: AbstractDate;
  metric?: Metric;
  clause?: Clause;
  comparisonPeriod?: ComparisonPeriod;
}

export class TermCardFragmentsFactory extends CardFragmentsFactory {
  static create(input: TermCardFactoryOptions) {
    const factory = new TermCardFragmentsFactory();
    return factory.getFragments(input);
  }
  private getFragments(input?: TermCardFactoryOptions) {
    return this.getComparaisonFragments(
      this.getFragment(input),
      input && has(input, "comparisonPeriod")
        ? input.comparisonPeriod
        : "previous_period"
    );
  }
  private getFragment(input?: TermCardFactoryOptions): SearchFragmentTerm {
    const term = input?.term || Facet.Platforms;
    const metric = input?.metric || Metric.Doc;
    return {
      request: {
        query: this.getQuery(input),
        // @ts-ignore
        aggs: {
          metrics: { metrics: [metric] },
          [term]:
            term !== Facet.Focuses
              ? {
                  metrics: [metric],
                  sort: {
                    on: metric,
                    order: "desc",
                  },
                  size: 100,
                  topHit: term === Facet.Product ? { size: 1 } : null,
                }
              : {
                  metrics: [metric],
                },
        },
      },
    };
  }
}

export class TermCardFactory
  implements ICardFactory<TermCard, TermCardFactoryOptions>
{
  public static getInstance() {
    return new TermCardFactory();
  }
  public fromCard(
    workspaceId: number,
    projectFocuses: number[],
    card: Card,
    input?: TermCardFactoryOptions & {
      dataviz?: Dataviz;
    }
  ) {
    const factory = new TermCardFragmentsFactory();
    const { from, to } = factory.getPeriod(card);
    return CardFactory.getInstance<TermCard>().createCard(
      workspaceId,
      projectFocuses,
      "term",
      card.title,
      TermCardFragmentsFactory.create({
        clause: factory.getClauseFromCard(card),
        from,
        to,
        term: factory.getTermFromCard(card, input) as TermCardTerm,
        metric: factory.getMetricFromCardAndInput(card, input),
        comparisonPeriod: factory.getComparaisonPeriod(
          card,
          "previous_period",
          input
        ),
      }),
      this.createDataviz(input?.dataviz || card.dataviz, input)
    );
  }
  public fromInput(
    workspaceId: number,
    projectFocuses: number[],
    title: string,
    input?: TermCardFactoryOptions & {
      dataviz?: TermCard["dataviz"]["name"];
      fullDataviz?: Dataviz;
    }
  ) {
    return CardFactory.getInstance<TermCard>().createCard(
      workspaceId,
      projectFocuses,
      "term",
      title,
      TermCardFragmentsFactory.create(input),
      this.createDataviz(
        input?.fullDataviz ??
          getDefaultDataviz(
            input?.dataviz || this.getDefaultDatavizFromTerm(input)
          ),
        input
      )
    );
  }
  public createDataviz(dataviz: Dataviz, input?: TermCardFactoryOptions) {
    if (isTermCardDataviz(dataviz)) {
      return dataviz;
    } else {
      return getDefaultDataviz(
        this.getDefaultDatavizFromTerm(input)
      ) as TermCard["dataviz"];
    }
  }
  private getDefaultDatavizFromTerm(
    input?: TermCardFactoryOptions
  ): TermCard["dataviz"]["name"] {
    if (this.isCloudTerm(input?.term || Facet.Platforms)) {
      return DatavizName.Cloud;
    } else {
      return DatavizName.HorizontalBarChart;
    }
  }
  private isCloudTerm(term: Facet) {
    return includes(
      [
        Facet.Hashtags,
        Facet.Mentions,
        Facet.Entities,
        Facet.EmojiCharts,
        Facet.Occupations,
        Facet.ImageRecognitionObjects,
        Facet.ImageRecognitionScenes,
        Facet.Affects,
        Facet.SignificantEntities,
        Facet.SignificantHashtags,
        Facet.SignificantMentions,
        Facet.SignificantNamedEntities,
        Facet.EmergingHashtags,
        Facet.EmergingMentions,
        Facet.EmergingNamedEntities,
      ],
      term
    );
  }
}
